import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Globe } from "lucide-react";

interface PublishedFileDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  isWorking?: boolean;
}

/**
 * Confirmation when deleting a published file record. Warns that the public
 * link will stop resolving.
 */
export function PublishedFileDeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  isWorking = false,
}: PublishedFileDeleteDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isWorking) onOpenChange(false);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Archive published file?</DialogTitle>
          <DialogDescription>
            This file is currently published. Archiving it will permanently
            break any public link pointing at it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2.5 rounded-md border border-lc-warning/60 bg-gold/10 p-3 text-lc-warning dark:border-lc-warning/30 dark:bg-lc-warning/10 dark:text-lc-warning">
          <Globe className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-0.5 text-sm">
            <div className="font-medium">Public link will stop resolving</div>
            <div className="text-xs opacity-90">
              Anyone visiting the published URL will see a not-found page.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isWorking}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void onConfirm()}
            disabled={isWorking}
          >
            {isWorking ? "Archiving…" : "Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
